/**
 * ListingCollectionPage — /listing-collection/:id
 *
 * Public, shareable page for one curated listing collection: banner, oversized
 * title, then a 2-column grid of product cards.
 *
 * Mirrors BusinessCollectionPage exactly, with businesses swapped for product
 * listings. Public on purpose — a shared link must open without login.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Bookmark, Pencil, Share2, Trash2 } from "lucide-react";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import {
  fetchListingCollectionById,
  deleteListingCollection,
} from "../../../services/thunks/ListingCollectionThunk";
import {
  setListingCollectionMeta,
  clearListingCollection,
  selectListingCollectionProducts,
  selectListingCollectionMeta,
  selectListingCollectionStatus,
  selectDeleteListingCollectionStatus,
} from "../../../dataManagement/uiDb/ListingCollectionDb";
import { ListingCollection } from "../../../interfaces/ListingCollectionObject";
import { ProductDetailsModal } from "../../../interfaces/ProductObject";
import genericUtility from "../../../services/utility/GenericUtility";
import { splitTitle, formatUpdated } from "../../../services/helpers/CollectionDisplayHelper";
import ShareProfileModal from "../../shared/ShareProfileModal/ShareProfileModal";
import ConfirmModal from "../../shared/ConfirmModal/ConfirmModal";
import SaveAsCollectionModal from "../../shared/SaveAsCollectionModal/SaveAsCollectionModal";
import { useCanSaveCollections } from "../../../hooks/useCanSaveCollections";
import CreateListingCollectionModal from "../../features/Admin/ListingCollections/CreateListingCollectionModal";
import CollectionProductCard from "./childComponents/CollectionProductCard";
import { useNotification } from "../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
// Intentional: reuses the identical layout CSS from BusinessCollectionPage.
// The coll-* class design system applies equally to both collection types.
import "../businessCollection/styles/BusinessCollectionPage.css";

const FALLBACK_TITLE = "Curated Listings";

const ListingCollectionPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const { triggerNotification } = useNotification();

  const products = useSelector(selectListingCollectionProducts);
  const { isLoading, hasLoaded, error, notFound } = useSelector(
    selectListingCollectionStatus
  );
  const collection = useSelector(selectListingCollectionMeta);
  const { isDeleting } = useSelector((state: RootState) =>
    selectDeleteListingCollectionStatus(state, id)
  );

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [barProgress, setBarProgress] = useState(0);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const canSaveCollections = useCanSaveCollections();

  const currentUserId = genericUtility.getCurrentUserId();
  const isOwner = !!collection && collection.creatorId === currentUserId;
  /** Curators and admins who don't own this collection can save a copy as their own. */
  const canSaveCopy = !!collection && canSaveCollections && !isOwner;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);

  const routerState = location.state as
    | { collection?: ListingCollection; backTo?: string }
    | null;
  const passedCollection = routerState?.collection;
  const backTo = routerState?.backTo;

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  useEffect(() => {
    if (passedCollection) dispatch(setListingCollectionMeta(passedCollection));
  }, [dispatch, passedCollection]);

  const load = useCallback(() => {
    if (id) {
      dispatch(fetchListingCollectionById({ collectionId: id, includeListingDetails: true }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => { dispatch(clearListingCollection()); }, [dispatch]);

  const handleScroll = useCallback(() => {
    const top = scrollerRef.current?.scrollTop ?? 0;
    const t = Math.max(0, Math.min(1, (top - 150) / 90));
    if (Math.abs(t - progressRef.current) > 0.01 || t === 0 || t === 1) {
      progressRef.current = t;
      setBarProgress(t);
    }
  }, []);

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteListingCollection(id)).unwrap();
      triggerNotification("Collection deleted.", NotificationType.SUCCESS);
      setIsDeleteConfirmOpen(false);
      navigate(backTo ?? "/my-profile?tab=admin&section=myListingCollections");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't delete this collection.";
      triggerNotification(msg, NotificationType.ERROR);
      setIsDeleteConfirmOpen(false);
    }
  };

  const openProduct = useCallback(
    (product: ProductDetailsModal) =>
      navigate(`/product-details/${product.productId}`),
    [navigate]
  );

  const title = collection?.name || FALLBACK_TITLE;
  const { head: titleHead, tail: titleTail, accentTail } = splitTitle(title);
  const hasBanner = Boolean(collection?.bannerUrl);
  const count = hasLoaded ? products.length : collection?.productIds.length ?? 0;
  const updated = formatUpdated(collection?.updatedAt);
  const shareUrl = `${window.location.origin}/listing-collection/${id}`;
  const gridStyle = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" } as const;

  return (
    <div className="coll-screen">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="coll-bar">
        <div className="coll-bar-plate" style={{ opacity: barProgress }} />
        <div className="coll-bar-row">
          <button
            type="button"
            className="coll-bar-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>

          <span
            className="coll-bar-title"
            style={{
              opacity: barProgress,
              transform: `translateY(${(1 - barProgress) * 6}px)`,
            }}
            aria-hidden={barProgress < 0.5}
          >
            {title}
          </span>

          <div className="coll-bar-actions">
            {isOwner && (
              <>
                <button
                  type="button"
                  className="coll-bar-btn"
                  onClick={() => setIsEditOpen(true)}
                  aria-label="Edit this collection"
                >
                  <Pencil size={17} />
                </button>
                <button
                  type="button"
                  className="coll-bar-btn"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  aria-label="Delete this collection"
                >
                  <Trash2 size={17} />
                </button>
              </>
            )}
            {canSaveCopy && (
              <button
                type="button"
                className="coll-bar-btn"
                onClick={() => setIsSaveOpen(true)}
                aria-label="Save this collection to my collections"
                title="Save collection"
              >
                <Bookmark size={17} />
              </button>
            )}
            <button
              type="button"
              className="coll-bar-btn"
              onClick={() => setIsShareOpen(true)}
              aria-label="Share this collection"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Scroller ─────────────────────────────────────────────────────── */}
      <div className="coll-scroll" ref={scrollerRef} onScroll={handleScroll}>
        <div className={`coll-banner${hasBanner ? "" : " coll-banner--empty"}`}>
          {collection?.bannerUrl && (
            <img src={collection.bannerUrl} alt="" loading="eager" />
          )}
          <div className="coll-banner-scrim" />
        </div>

        <div className="coll-title-block">
          <div className="coll-kicker">
            <span className="coll-kicker-bar" />
            Curated listings
          </div>

          <h1 className="coll-h1">
            {titleHead && (
              <>
                {titleHead}
                <br />
              </>
            )}
            <span className={accentTail ? "coll-h1-accent" : undefined}>
              {titleTail}
            </span>
          </h1>

          <div className="coll-meta">
            <span className="coll-meta-strong">
              {count} {count === 1 ? "listing" : "listings"}
            </span>
            {collection?.creatorName && (
              <>
                <span className="coll-meta-sep">/</span>
                <span>Curated by {collection.creatorName}</span>
              </>
            )}
            {updated && (
              <>
                <span className="coll-meta-sep">/</span>
                <span>Updated {updated}</span>
              </>
            )}
          </div>
        </div>

        <div className="coll-content">
          {isLoading && (
            <div className="coll-grid" style={gridStyle} aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="coll-skeleton" key={i}>
                  <div className="coll-skeleton-logo" />
                  <div className="coll-skeleton-line" />
                  <div className="coll-skeleton-line coll-skeleton-line--short" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && notFound && (
            <div className="coll-state" role="alert">
              <p className="coll-state-text">
                This collection may have been removed, or the link is incorrect.
              </p>
              <button
                type="button"
                className="coll-state-btn"
                onClick={() => navigate("/search")}
              >
                Explore listings
              </button>
            </div>
          )}

          {!isLoading && error && !notFound && (
            <div className="coll-state" role="alert">
              <p className="coll-state-text">{error}</p>
              <button type="button" className="coll-state-btn" onClick={load}>
                Try again
              </button>
            </div>
          )}

          {!isLoading && !error && products.length === 0 && !notFound && (
            <div className="coll-state">
              <p className="coll-state-text">
                No listings in this collection yet.
              </p>
              <button
                type="button"
                className="coll-state-btn"
                onClick={() => navigate("/search")}
              >
                Explore listings
              </button>
            </div>
          )}

          {!isLoading && !error && products.length > 0 && (
            <div className="coll-grid" style={gridStyle}>
              {products.map((product) => (
                <CollectionProductCard
                  key={product.productId}
                  product={product}
                  onOpen={openProduct}
                />
              ))}
            </div>
          )}
        </div>

        <div className="coll-footer">
          <span className="coll-footer-rule" />
          <p className="coll-footer-text">
            Explore more listings and discover what's available near you on NineFinds.
          </p>
        </div>
      </div>

      <ShareProfileModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        profileUrl={shareUrl}
        title={title}
        subtitle={`Check out ${title} on NineFinds`}
        shareMode="qr"
      />

      {isOwner && collection && (
        <CreateListingCollectionModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          editCollection={{ collection, products }}
          onSaved={load}
        />
      )}

      {collection && (
        // A snapshot copy: later changes to the original do not flow into it.
        <SaveAsCollectionModal
          isOpen={isSaveOpen}
          initialName={collection.name || FALLBACK_TITLE}
          productIds={
            collection.productIds.length
              ? collection.productIds
              : products.map((product) => product.productId)
          }
          bannerUrl={collection.bannerUrl}
          onClose={() => setIsSaveOpen(false)}
        />
      )}

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        tone="danger"
        title="Delete this collection?"
        message={
          <>
            This will permanently remove <strong>{title}</strong>. This cannot
            be undone.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default ListingCollectionPage;
